import React, { useState, useMemo, useCallback } from 'react';
import { CBA, getTeamTier, getHardCap, getTierLabel, getMaxIncoming, validateTrade, executeTrade } from './tradeEngine';
import DRAFT_PICKS, { hasNoTradeClause } from './draftPicks';
import { getPlayerRating, getRatingColor } from './playerRatings';

// ============================================================================
// CUSTOM TEAM PICKER
// ============================================================================
const TeamPicker = ({ value, options, onChange, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = value ? options.find(t => t.abbr === value) : null;

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="team-picker" ref={ref}>
      <button className="team-picker-btn" onClick={() => setOpen(!open)}>
        {selected ? (
          <>
            <img src={teamLogoUrl(selected.nbaId)} alt="" className="team-picker-logo" />
            <span className="team-picker-name">{selected.name}</span>
            <span className="team-picker-abbr">{selected.abbr}</span>
          </>
        ) : (
          <span className="team-picker-placeholder">{placeholder}</span>
        )}
        <span className="team-picker-caret">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="team-picker-dropdown">
          {value && (
            <button className="team-picker-option clear" onClick={() => { onChange(null); setOpen(false); }}>
              Clear Selection
            </button>
          )}
          {options.map(t => (
            <button
              key={t.abbr}
              className={`team-picker-option ${t.abbr === value ? 'active' : ''}`}
              onClick={() => { onChange(t.abbr); setOpen(false); }}
            >
              <img src={teamLogoUrl(t.nbaId)} alt="" className="team-picker-opt-logo" />
              <span className="team-picker-opt-name">{t.name}</span>
              <span className="team-picker-opt-abbr">{t.abbr}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TRADE MACHINE COMPONENT
// ============================================================================

const teamLogoUrl = (nbaId) => `https://cdn.nba.com/logos/nba/${nbaId}/global/L/logo.svg`;
const formatSalary = (n) => {
  if (!n && n !== 0) return '\u2014';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const formatSalaryFull = (n) => n ? `$${n.toLocaleString()}` : '\u2014';
const getInitials = (name) => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length <= 1) return (parts[0] || '?')[0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const playerHeadshotUrl = (nbaComId) => `https://cdn.nba.com/headshots/nba/latest/1040x760/${nbaComId}.png`;

// Small headshot with initials fallback
const MiniHead = ({ name, nbaComId }) => {
  const [fail, setFail] = React.useState(false);
  if (fail || !nbaComId) return <div className="trade-player-initials">{getInitials(name)}</div>;
  return (
    <img
      src={playerHeadshotUrl(nbaComId)}
      alt=""
      className="trade-player-img"
      onError={() => setFail(true)}
    />
  );
};

// ============================================================================
// TEAM COLUMN
// ============================================================================
const TeamColumn = ({ team, teamInfo, salaryData, sending, sendingPicks, onTogglePlayer, onTogglePick, otherTeams, destinations, pickDestinations, onSetDestination, onSetPickDestination, isMultiTeam, allTeams, tradeHistory }) => {
  const [search, setSearch] = useState('');
  const players = salaryData?.players || [];
  const payroll = salaryData?.payroll || 0;
  const tier = getTeamTier(payroll);
  const hardCap = getHardCap(team.abbr);

  // Build tradeable picks by overlaying tradeHistory onto static DRAFT_PICKS
  const tradeablePicks = useMemo(() => {
    const basePicks = JSON.parse(JSON.stringify(DRAFT_PICKS[team.abbr]?.picks || []));

    // Apply trade history — mark sent picks as traded, add received picks
    for (const trade of (tradeHistory || [])) {
      if (!trade.picks) continue;
      const teams = trade.teams || [];
      const pDests = trade.pickDestinations || {};
      for (const [sender, sentPicks] of Object.entries(trade.picks)) {
        for (const pick of (sentPicks || [])) {
          if (!pick.year || !pick.round) continue;
          let receiver;
          if (teams.length === 2) {
            receiver = teams.find(t => t !== sender) || '';
          } else {
            const senderDests = pDests[sender] || {};
            receiver = senderDests[pick.id] || Object.values(senderDests)[0] || teams.find(t => t !== sender) || '';
          }
          if (sender === team.abbr) {
            // This team sent this pick away — mark it traded
            const idx = basePicks.findIndex(p =>
              p.year === pick.year && p.round === pick.round &&
              (p.status === 'own' || p.status === 'incoming')
            );
            if (idx !== -1) {
              basePicks[idx] = { ...basePicks[idx], status: 'traded', to: receiver };
            }
          } else if (receiver === team.abbr) {
            // This team received this pick — add it as incoming
            basePicks.push({
              year: pick.year, round: pick.round, status: 'incoming',
              from: sender, note: 'Acquired via trade',
            });
          }
        }
      }
    }

    return basePicks
      .filter(p => (p.status === 'own' || p.status === 'incoming'))
      .map((p, i) => ({
        ...p,
        id: `${team.abbr}-${p.year}-R${p.round}-${p.status}-${i}`,
        label: p.status === 'incoming'
          ? `${p.year} ${p.round === 1 ? '1st' : '2nd'} (via ${p.from})`
          : `${p.year} ${p.round === 1 ? '1st' : '2nd'}`,
      }));
  }, [team.abbr, tradeHistory]);

  const sendingNames = new Set(sending.map(p => p.name));
  const sendingPickIds = new Set((sendingPicks || []).map(p => p.id));
  const outgoingTotal = sending.reduce((sum, p) => sum + (p.salary2526 || 0), 0);

  const filtered = players.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const tierColors = {
    under_cap: 'var(--accent-green)',
    over_cap: 'var(--accent-gold)',
    first_apron: 'var(--accent-red)',
    second_apron: '#8b0000',
  };

  return (
    <div className="trade-column">
      {/* Team Header */}
      <div className="trade-col-header">
        <img src={teamLogoUrl(team.nbaId)} alt={team.abbr} className="trade-col-logo" />
        <div className="trade-col-info">
          <div className="trade-col-name">{team.name}</div>
          <div className="trade-col-meta">
            Payroll: <strong>{formatSalary(payroll)}</strong>
          </div>
        </div>
      </div>

      {/* Tier Badge */}
      <div className="trade-tier-row">
        <span className="trade-tier-badge" style={{ borderColor: tierColors[tier], color: tierColors[tier] }}>
          {getTierLabel(tier)}
        </span>
        {hardCap && (
          <span className="trade-tier-badge" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)' }}>
            Hard Cap: {formatSalary(hardCap)}
          </span>
        )}
      </div>

      {/* Sending Section */}
      {(sending.length > 0 || (sendingPicks || []).length > 0) && (
        <div className="trade-sending">
          <div className="trade-sending-label">
            SENDING OUT {outgoingTotal > 0 ? `\u00B7 ${formatSalary(outgoingTotal)}` : ''}
            {(sendingPicks || []).length > 0 ? ` + ${(sendingPicks || []).length} pick${(sendingPicks || []).length > 1 ? 's' : ''}` : ''}
          </div>
          {sending.map(p => {
            const ntc = hasNoTradeClause(p.name);
            return (
              <div key={p.name} className="trade-sending-player" onClick={() => onTogglePlayer(team.abbr, p)}>
                <MiniHead name={p.name} nbaComId={p.nbaComId} />
                <div className="trade-sending-info">
                  <div className="trade-sending-name">
                    {p.name}
                    <span className="ovr-badge-sm" style={{ borderLeftColor: getRatingColor(getPlayerRating(p.name)), marginLeft: 6 }}>{getPlayerRating(p.name)}</span>
                    {ntc && <span className="trade-ntc-badge" title={ntc.type}>NTC</span>}
                  </div>
                  <div className="trade-sending-sal">{formatSalary(p.salary2526)}</div>
                </div>
                {isMultiTeam && otherTeams.length > 1 && (
                  <select
                    className="trade-dest-select"
                    value={destinations?.[team.abbr]?.[p.name] || otherTeams[0]?.abbr || ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSetDestination(team.abbr, p.name, e.target.value);
                    }}
                  >
                    {otherTeams.map(ot => (
                      <option key={ot.abbr} value={ot.abbr}>{ot.abbr}</option>
                    ))}
                  </select>
                )}
                <button className="trade-remove-btn" onClick={(e) => { e.stopPropagation(); onTogglePlayer(team.abbr, p); }}>
                  &times;
                </button>
              </div>
            );
          })}
          {(sendingPicks || []).map(pick => (
            <div key={pick.id} className="trade-sending-player trade-sending-pick" onClick={() => onTogglePick(team.abbr, pick)}>
              <div className="trade-pick-icon">&#9733;</div>
              <div className="trade-sending-info">
                <div className="trade-sending-name">{pick.label}</div>
                <div className="trade-sending-sal">{pick.note || ''}</div>
              </div>
              {isMultiTeam && otherTeams.length > 1 && (
                <select
                  className="trade-dest-select"
                  value={pickDestinations?.[team.abbr]?.[pick.id] || otherTeams[0]?.abbr || ''}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSetPickDestination(team.abbr, pick.id, e.target.value);
                  }}
                >
                  {otherTeams.map(ot => (
                    <option key={ot.abbr} value={ot.abbr}>{ot.abbr}</option>
                  ))}
                </select>
              )}
              <button className="trade-remove-btn" onClick={(e) => { e.stopPropagation(); onTogglePick(team.abbr, pick); }}>
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Roster Search */}
      <div className="trade-search-wrap">
        <input
          className="trade-search"
          type="text"
          placeholder="Search roster..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Roster List */}
      <div className="trade-roster">
        {filtered.map(p => {
          const isSending = sendingNames.has(p.name);
          const ntc = hasNoTradeClause(p.name);
          return (
            <div
              key={p.name}
              className={`trade-roster-row ${isSending ? 'sending' : ''} ${p.isTwoWay ? 'two-way' : ''} ${p.isDead ? 'dead-cap' : ''}`}
              onClick={() => !p.isDead && onTogglePlayer(team.abbr, p)}
              style={p.isDead ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              <MiniHead name={p.name} nbaComId={p.nbaComId} />
              <div className="trade-roster-name">
                {p.name}
                {p.isTwoWay && <span className="trade-tw-badge">2W</span>}
                {p.isDead && <span className="trade-tw-badge" style={{ background: '#8b0000' }}>DEAD CAP</span>}
                {ntc && <span className="trade-ntc-badge" title={ntc.type}>NTC</span>}
              </div>
              <span className="ovr-badge-sm" style={{ borderLeftColor: getRatingColor(getPlayerRating(p.name)) }}>{getPlayerRating(p.name)}</span>
              <div className="trade-roster-sal">{formatSalary(p.salary2526)}</div>
            </div>
          );
        })}
      </div>

      {/* Draft Picks - Compact */}
      {tradeablePicks.length > 0 && (
        <div className="trade-picks-section">
          <div className="trade-picks-label">DRAFT PICKS</div>
          <div className="trade-picks-grid">
            {tradeablePicks.map(pick => {
              const isSending = sendingPickIds.has(pick.id);
              const fromAbbr = pick.status === 'incoming' && pick.from ? pick.from.split('/')[0] : null;
              const fromTeam = fromAbbr ? allTeams?.find(t => t.abbr === fromAbbr) : null;
              return (
                <button
                  key={pick.id}
                  className={`trade-pick-chip ${isSending ? 'sending' : ''} ${pick.round === 1 ? 'r1' : 'r2'}`}
                  onClick={() => onTogglePick(team.abbr, pick)}
                  title={pick.note || pick.label}
                >
                  {fromTeam && (
                    <img src={teamLogoUrl(fromTeam.nbaId)} alt="" className="trade-pick-chip-logo" />
                  )}
                  <span className="trade-pick-chip-label">
                    {pick.round === 1 ? '1st' : '2nd'} '{String(pick.year).slice(2)}
                  </span>
                  {pick.status === 'incoming' && <span className="trade-pick-chip-via">via {pick.from?.split('/')[0]}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TRADE VERDICT
// ============================================================================
const TradeVerdict = ({ result, onExecute, onReset, suggestions, onApplySuggestion }) => {
  if (!result) return null;
  const { valid, errors, warnings, breakdown } = result;

  return (
    <div className={`trade-verdict ${valid ? 'valid' : 'invalid'}`}>
      <div className="trade-verdict-header">
        <div className={`trade-verdict-badge ${valid ? 'valid' : 'invalid'}`}>
          {valid ? '✓ TRADE WORKS' : '✗ TRADE FAILS'}
        </div>
        {valid && (
          <button className="trade-execute-btn" onClick={onExecute}>
            Execute Trade
          </button>
        )}
      </div>

      {/* Per-team breakdown */}
      <div className="trade-verdict-grid">
        {Object.entries(breakdown).map(([abbr, bd]) => (
          <div key={abbr} className="trade-verdict-team">
            <div className="trade-verdict-team-name">{abbr}</div>
            <div className="trade-verdict-row">
              <span>Sends</span>
              <span className="mono">{formatSalary(bd.outgoingTotal)}</span>
            </div>
            <div className="trade-verdict-row">
              <span>Receives</span>
              <span className="mono">{formatSalary(bd.incomingTotal)}</span>
            </div>
            <div className="trade-verdict-row">
              <span>Max Allowed</span>
              <span className="mono">{bd.maxIncoming ? formatSalary(bd.maxIncoming) : '\u2014'}</span>
            </div>
            <div className="trade-verdict-row">
              <span>Method</span>
              <span className="mono small">{bd.matchMethod}</span>
            </div>
            <div className="trade-verdict-divider" />
            <div className="trade-verdict-row">
              <span>Current Payroll</span>
              <span className="mono">{formatSalary(bd.currentPayroll)}</span>
            </div>
            <div className="trade-verdict-row">
              <span>Post-Trade</span>
              <span className="mono" style={{
                color: bd.postTradePayroll > CBA.luxuryTax ? 'var(--accent-red)' : 'var(--accent-green)'
              }}>
                {formatSalary(bd.postTradePayroll)}
              </span>
            </div>
            <div className="trade-verdict-row">
              <span>Net Change</span>
              <span className="mono" style={{
                color: bd.netChange > 0 ? 'var(--accent-red)' : 'var(--accent-green)'
              }}>
                {bd.netChange > 0 ? '+' : ''}{formatSalary(bd.netChange)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="trade-verdict-errors">
          {errors.map((e, i) => (
            <div key={i} className="trade-verdict-error">{e}</div>
          ))}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="trade-verdict-warnings">
          {warnings.map((w, i) => (
            <div key={i} className="trade-verdict-warning">⚠ {w}</div>
          ))}
        </div>
      )}

      {/* Suggest a Fix */}
      {!valid && suggestions && suggestions.length > 0 && (
        <div className="trade-suggest-fix">
          <div className="trade-suggest-fix-title">💡 SUGGESTED FIX</div>
          {suggestions.map((s, i) => (
            <div key={i} className="trade-suggest-fix-item">
              <div className="trade-suggest-fix-desc">
                Add from <strong>{s.team}</strong>: {s.players.map(p => `${p.name} (${formatSalary(p.salary2526)})`).join(', ')}
              </div>
              <button className="trade-suggest-fix-btn" onClick={() => onApplySuggestion(s)}>
                Apply Fix
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TRADE HISTORY
// ============================================================================
const TradeHistory = ({ history }) => {
  if (history.length === 0) return null;
  return (
    <div className="trade-history">
      <div className="trade-history-title">Executed Trades ({history.length})</div>
      {history.map((trade, i) => (
        <div key={i} className="trade-history-item">
          <div className="trade-history-num">Trade #{i + 1}</div>
          {Object.entries(trade.packages || {}).map(([abbr, players]) => {
            const teamPicks = trade.picks?.[abbr] || [];
            const items = [
              ...players.map(p => p.name),
              ...teamPicks.map(pk => pk.label),
            ];
            return (
              <div key={abbr} className="trade-history-team">
                <strong>{abbr}</strong> sends: {items.join(', ') || 'Nothing'}
              </div>
            );
          })}
          {/* Show pick-only teams that aren't in packages */}
          {trade.picks && Object.entries(trade.picks).filter(([abbr]) => !trade.packages?.[abbr]).map(([abbr, picks]) => (
            <div key={abbr} className="trade-history-team">
              <strong>{abbr}</strong> sends: {picks.map(pk => pk.label).join(', ')}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN TRADE MACHINE
// ============================================================================
// ============================================================================
// TRADE FINDER
// ============================================================================
const TradeFinder = ({ allTeams, salaryData, seasonPhase, myTeam, onLoadTrade }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('suggestions'); // 'suggestions' | 'search'
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Generate matching trades for a given player on a given team
  const findTradesForPlayer = useCallback((targetPlayer, targetTeam, requireBalance = false) => {
    const targetSalary = targetPlayer.salary2526 || 0;
    const targetOvr = getPlayerRating(targetPlayer.name) || 70;
    const trades = [];

    for (const [abbr, team] of Object.entries(salaryData || {})) {
      if (abbr === targetTeam || !team?.players) continue;
      const payroll = team.payroll || 0;
      const tier = getTeamTier(payroll);

      // Single player matches
      for (const p of team.players) {
        if (p.isDead || p.isTwoWay || (p.salary2526 || 0) < 1000000) continue;
        const outgoing = p.salary2526 || 0;
        const maxIn = tier === 'under_cap'
          ? (CBA.salaryCap - payroll) + outgoing
          : getMaxIncoming(outgoing, tier);
        const senderPayroll = (salaryData[targetTeam]?.payroll || 0);
        const senderTier = getTeamTier(senderPayroll);
        const senderMaxIn = senderTier === 'under_cap'
          ? (CBA.salaryCap - senderPayroll) + targetSalary
          : getMaxIncoming(targetSalary, senderTier);

        if (targetSalary <= maxIn && outgoing <= senderMaxIn) {
          const partnerOvr = getPlayerRating(p.name) || 70;
          const ovrGap = Math.abs(targetOvr - partnerOvr);
          // For suggestions, skip wildly unbalanced trades
          if (requireBalance && ovrGap > 8) continue;
          trades.push({
            partnerTeam: abbr,
            partnerSends: [p],
            targetSends: [targetPlayer],
            salaryDiff: Math.abs(targetSalary - outgoing),
            quality: partnerOvr,
            targetOvr,
            ovrGap,
          });
        }
      }

      // Two-player combos
      if (tier !== 'second_apron') {
        const tradeable = team.players
          .filter(p => !p.isDead && !p.isTwoWay && (p.salary2526 || 0) >= 1000000)
          .sort((a, b) => (b.salary2526 || 0) - (a.salary2526 || 0))
          .slice(0, 8);
        for (let i = 0; i < tradeable.length; i++) {
          for (let j = i + 1; j < Math.min(tradeable.length, i + 4); j++) {
            const combined = (tradeable[i].salary2526 || 0) + (tradeable[j].salary2526 || 0);
            const maxIn = tier === 'under_cap'
              ? (CBA.salaryCap - payroll) + combined
              : getMaxIncoming(combined, tier);
            const senderPayroll = (salaryData[targetTeam]?.payroll || 0);
            const senderTier = getTeamTier(senderPayroll);
            const senderMaxIn = senderTier === 'under_cap'
              ? (CBA.salaryCap - senderPayroll) + targetSalary
              : getMaxIncoming(targetSalary, senderTier);
            if (targetSalary <= maxIn && combined <= senderMaxIn) {
              const bestOvr = Math.max(getPlayerRating(tradeable[i].name) || 70, getPlayerRating(tradeable[j].name) || 70);
              const avgOvr = ((getPlayerRating(tradeable[i].name) || 70) + (getPlayerRating(tradeable[j].name) || 70)) / 2;
              const ovrGap = Math.abs(targetOvr - bestOvr);
              if (requireBalance && ovrGap > 10) continue;
              trades.push({
                partnerTeam: abbr, partnerSends: [tradeable[i], tradeable[j]], targetSends: [targetPlayer],
                salaryDiff: Math.abs(targetSalary - combined), quality: avgOvr, targetOvr, ovrGap,
              });
            }
          }
        }
      }
    }
    // For suggestions: sort by OVR closeness first, then quality
    if (requireBalance) {
      trades.sort((a, b) => a.ovrGap - b.ovrGap || b.quality - a.quality);
    } else {
      trades.sort((a, b) => (b.quality - b.salaryDiff / 10000000) - (a.quality - a.salaryDiff / 10000000));
    }
    return trades.slice(0, 8);
  }, [salaryData]);

  // Auto-generate suggestions for user's team
  const suggestions = useMemo(() => {
    if (!myTeam || !salaryData?.[myTeam]?.players) return [];
    const myPlayers = salaryData[myTeam].players
      .filter(p => !p.isDead && !p.isTwoWay && (p.salary2526 || 0) >= 3000000)
      .sort((a, b) => (b.salary2526 || 0) - (a.salary2526 || 0))
      .slice(0, 6);

    const allSuggestions = [];
    for (const player of myPlayers) {
      const trades = findTradesForPlayer(player, myTeam, true); // requireBalance = true
      if (trades.length > 0) {
        allSuggestions.push(...trades.slice(0, 2).map(t => ({ ...t, sourcePlayer: player })));
      }
    }
    // Deduplicate by partner team + incoming player combo and sort by OVR closeness
    const seen = new Set();
    return allSuggestions.filter(t => {
      const key = `${t.partnerTeam}-${t.partnerSends.map(p => p.name).join(',')}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => a.ovrGap - b.ovrGap || b.quality - a.quality).slice(0, 10);
  }, [myTeam, salaryData, findTradesForPlayer]);

  const findTrades = useCallback(() => {
    if (!searchName.trim()) return;
    setSearching(true);
    let targetPlayer = null;
    let targetTeam = null;
    const q = searchName.toLowerCase();
    for (const [abbr, team] of Object.entries(salaryData || {})) {
      if (!team?.players) continue;
      for (const p of team.players) {
        if (p.isDead || p.isTwoWay) continue;
        if (p.name.toLowerCase().includes(q)) { targetPlayer = p; targetTeam = abbr; break; }
      }
      if (targetPlayer) break;
    }
    if (!targetPlayer || !targetTeam) {
      setSearchResults({ error: `No tradeable player found matching "${searchName}"` });
      setSearching(false);
      return;
    }

    let trades = findTradesForPlayer(targetPlayer, targetTeam, true); // Always require OVR balance

    // If user has a team selected, only show trades that involve their team
    if (myTeam) {
      if (targetTeam === myTeam) {
        // Player is on user's team — all results already involve myTeam, keep as-is
      } else {
        // Player is on another team — only show trades where myTeam is the partner
        trades = trades.filter(t => t.partnerTeam === myTeam);
      }
    }

    setSearchResults({ player: targetPlayer, team: targetTeam, trades });
    setSearching(false);
  }, [searchName, salaryData, myTeam, findTradesForPlayer]);

  if (seasonPhase === 'postDeadline' || seasonPhase === 'playoffs') return null;

  const teamInfo = (abbr) => allTeams.find(t => t.abbr === abbr);
  const formatSal = (v) => `$${(v / 1000000).toFixed(1)}M`;
  const myTeamInfo = teamInfo(myTeam);

  const renderTradeCard = (trade, resultsTeam, i) => {
    const partner = teamInfo(trade.partnerTeam);
    return (
      <div key={i} className="trade-finder-result">
        <div className="trade-finder-result-header">
          <img src={teamLogoUrl(partner?.nbaId)} alt="" style={{ width: 20, height: 20 }} />
          <span className="trade-finder-result-team">{partner?.name || trade.partnerTeam}</span>
          {trade.ovrGap !== undefined && (
            <span className="trade-finder-ovr-gap" style={{ color: trade.ovrGap <= 5 ? 'var(--accent-green)' : trade.ovrGap <= 10 ? 'var(--accent-gold)' : 'var(--accent-red)' }}>
              {trade.ovrGap <= 5 ? 'Fair' : trade.ovrGap <= 10 ? 'Close' : 'Gap: ' + trade.ovrGap}
            </span>
          )}
        </div>
        <div className="trade-finder-result-body">
          <div className="trade-finder-result-col">
            <div className="trade-finder-col-label">{partner?.name} sends</div>
            {trade.partnerSends.map(p => {
              const ovr = getPlayerRating(p.name) || 70;
              return (
                <div key={p.name} className="trade-finder-player-row">
                  <span className="trade-finder-player-ovr" style={{ color: getRatingColor(ovr) }}>{ovr}</span>
                  <span className="trade-finder-player-name">{p.name}</span>
                  <span className="trade-finder-player-sal">{formatSal(p.salary2526)}</span>
                </div>
              );
            })}
          </div>
          <div className="trade-finder-result-arrow">for</div>
          <div className="trade-finder-result-col">
            <div className="trade-finder-col-label">{teamInfo(resultsTeam)?.name || resultsTeam} sends</div>
            {trade.targetSends.map(p => {
              const ovr = getPlayerRating(p.name) || 70;
              return (
                <div key={p.name} className="trade-finder-player-row">
                  <span className="trade-finder-player-ovr" style={{ color: getRatingColor(ovr) }}>{ovr}</span>
                  <span className="trade-finder-player-name">{p.name}</span>
                  <span className="trade-finder-player-sal">{formatSal(p.salary2526)}</span>
                </div>
              );
            })}
          </div>
        </div>
        <button className="trade-finder-load-btn" onClick={() => {
          onLoadTrade(resultsTeam, trade.partnerTeam, trade.targetSends, trade.partnerSends);
        }}>Load into Trade Machine</button>
      </div>
    );
  };

  return (
    <div className="trade-finder-section">
      <button className="trade-finder-toggle" onClick={() => setOpen(!open)}>
        <span className="trade-finder-toggle-label">Trade Finder</span>
        <span className="trade-finder-toggle-desc">{myTeam ? `Suggestions for ${myTeamInfo?.name || myTeam} and player search` : 'Search for valid trades involving any player'}</span>
        <span className="trade-finder-toggle-arrow">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="trade-finder-body">
          {/* Tab bar */}
          {myTeam && (
            <div className="trade-finder-tabs">
              <button className={`trade-finder-tab ${tab === 'suggestions' ? 'active' : ''}`} onClick={() => setTab('suggestions')}>
                Suggested Trades
              </button>
              <button className={`trade-finder-tab ${tab === 'search' ? 'active' : ''}`} onClick={() => setTab('search')}>
                Player Search
              </button>
            </div>
          )}

          {/* Suggestions tab */}
          {(tab === 'suggestions' && myTeam) && (
            <div className="trade-finder-results">
              {suggestions.length === 0 ? (
                <div className="trade-finder-empty">No valid trades found for your team's top contracts. This can happen when salary matching is tight.</div>
              ) : (
                <>
                  <div className="trade-finder-results-header">
                    <span>{suggestions.length} suggested trade{suggestions.length !== 1 ? 's' : ''} for </span>
                    <strong>{myTeamInfo?.name || myTeam}</strong>
                  </div>
                  {suggestions.map((trade, i) => renderTradeCard(trade, myTeam, i))}
                </>
              )}
            </div>
          )}

          {/* Search tab */}
          {(tab === 'search' || !myTeam) && (
            <>
              <div className="trade-finder-search">
                <input
                  type="text"
                  className="trade-finder-input"
                  placeholder={myTeam ? `Search player to trade ${myTeamInfo?.name ? 'with ' + myTeamInfo.name : ''}...` : 'Enter player name...'}
                  value={searchName}
                  onChange={e => setSearchName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && findTrades()}
                />
                <button className="trade-finder-btn" onClick={findTrades} disabled={searching || !searchName.trim()}>
                  {searching ? 'Searching...' : 'Find Trades'}
                </button>
              </div>
              {searchResults?.error && (
                <div className="trade-finder-empty">{searchResults.error}</div>
              )}
              {searchResults?.trades && (
                <div className="trade-finder-results">
                  <div className="trade-finder-results-header">
                    <span>{searchResults.trades.length} valid trade{searchResults.trades.length !== 1 ? 's' : ''} found for </span>
                    <strong>{searchResults.player.name}</strong>
                    <span> ({formatSal(searchResults.player.salary2526)})</span>
                    {myTeam && <span> involving {myTeamInfo?.name || myTeam}</span>}
                  </div>
                  {searchResults.trades.length === 0 && (
                    <div className="trade-finder-empty">
                      {myTeam
                        ? `No valid salary-matching trades found between ${myTeamInfo?.name || myTeam} and ${searchResults.player.name}'s team.`
                        : 'No valid salary-matching trades found for this player.'}
                    </div>
                  )}
                  {searchResults.trades.map((trade, i) => renderTradeCard(trade, searchResults.team, i))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN TRADE MACHINE
// ============================================================================
const TradeMachine = ({ allTeams, salaryData, onTradeExecuted, tradeHistory = [], seasonPhase = 'regular', myTeam = '' }) => {
  const [selectedTeams, setSelectedTeams] = useState([null, null]);
  const [packages, setPackages] = useState({});
  const [pickPackages, setPickPackages] = useState({});
  const [destinations, setDestinations] = useState({});
  const [pickDestinations, setPickDestinations] = useState({});
  const [ruleViolation, setRuleViolation] = useState(null);
  const isMultiTeam = selectedTeams.filter(Boolean).length > 2;

  // Reset packages when teams change
  const handleTeamSelect = useCallback((index, abbr) => {
    setSelectedTeams(prev => {
      const next = [...prev];
      const oldAbbr = next[index];
      next[index] = abbr;

      setPackages(p => {
        const np = { ...p };
        if (oldAbbr) delete np[oldAbbr];
        return np;
      });
      setPickPackages(p => {
        const np = { ...p };
        if (oldAbbr) delete np[oldAbbr];
        return np;
      });
      setDestinations(d => {
        const nd = { ...d };
        if (oldAbbr) delete nd[oldAbbr];
        return nd;
      });

      return next;
    });
  }, []);

  const addTeam = useCallback(() => {
    if (selectedTeams.length < 6) setSelectedTeams(prev => [...prev, null]);
  }, [selectedTeams.length]);

  const removeTeam = useCallback((index) => {
    setSelectedTeams(prev => {
      const next = [...prev];
      const removed = next[index];
      next.splice(index, 1);
      if (next.length < 2) next.push(null);

      setPackages(p => {
        const np = { ...p };
        if (removed) delete np[removed];
        return np;
      });
      setPickPackages(p => {
        const np = { ...p };
        if (removed) delete np[removed];
        return np;
      });
      setDestinations(d => {
        const nd = { ...d };
        if (removed) delete nd[removed];
        return nd;
      });

      return next;
    });
  }, []);

  const togglePlayer = useCallback((teamAbbr, player) => {
    if (player.isDead) return; // Dead cap cannot be traded
    setPackages(prev => {
      const teamPkg = prev[teamAbbr] || [];
      const exists = teamPkg.find(p => p.name === player.name);
      if (exists) {
        return { ...prev, [teamAbbr]: teamPkg.filter(p => p.name !== player.name) };
      } else {
        return { ...prev, [teamAbbr]: [...teamPkg, player] };
      }
    });
  }, []);

  const togglePick = useCallback((teamAbbr, pick) => {
    // Stepien Rule: can't trade 1st rounders in consecutive years if adjacent year's 1st is already gone
    if (pick.round === 1) {
      // Build adjusted picks from static + tradeHistory overlay
      const basePicks = JSON.parse(JSON.stringify(DRAFT_PICKS[teamAbbr]?.picks || []));
      for (const trade of (tradeHistory || [])) {
        if (!trade.picks) continue;
        const teams = trade.teams || [];
        const pDests = trade.pickDestinations || {};
        for (const [sender, sentPicks] of Object.entries(trade.picks)) {
          for (const p of (sentPicks || [])) {
            if (!p.year || !p.round) continue;
            let receiver;
            if (teams.length === 2) receiver = teams.find(t => t !== sender) || '';
            else { const sd = pDests[sender] || {}; receiver = sd[p.id] || Object.values(sd)[0] || teams.find(t => t !== sender) || ''; }
            if (sender === teamAbbr) {
              const idx = basePicks.findIndex(bp => bp.year === p.year && bp.round === p.round && (bp.status === 'own' || bp.status === 'incoming'));
              if (idx !== -1) basePicks[idx] = { ...basePicks[idx], status: 'traded' };
            } else if (receiver === teamAbbr) {
              basePicks.push({ year: p.year, round: p.round, status: 'incoming', from: sender });
            }
          }
        }
      }

      const prevYearTraded = basePicks.some(p => p.year === pick.year - 1 && p.round === 1 && p.status === 'traded');
      const nextYearTraded = basePicks.some(p => p.year === pick.year + 1 && p.round === 1 && p.status === 'traded');
      const currentPicks = pickPackages[teamAbbr] || [];
      const prevYearInTrade = currentPicks.some(p => p.year === pick.year - 1 && p.round === 1);
      const nextYearInTrade = currentPicks.some(p => p.year === pick.year + 1 && p.round === 1);

      if (prevYearTraded || nextYearTraded || prevYearInTrade || nextYearInTrade) {
        const existing = currentPicks.find(p => p.id === pick.id);
        if (!existing) {
          setRuleViolation(`Stepien Rule: ${teamAbbr} cannot trade their ${pick.year} 1st round pick — they don't control an adjacent year's 1st round pick.`);
          return;
        }
      }
    }

    setPickPackages(prev => {
      const teamPicks = prev[teamAbbr] || [];
      const exists = teamPicks.find(p => p.id === pick.id);
      if (exists) {
        return { ...prev, [teamAbbr]: teamPicks.filter(p => p.id !== pick.id) };
      } else {
        return { ...prev, [teamAbbr]: [...teamPicks, pick] };
      }
    });
  }, [pickPackages, tradeHistory]);

  const setDestination = useCallback((fromAbbr, playerName, toAbbr) => {
    setDestinations(prev => ({
      ...prev,
      [fromAbbr]: { ...(prev[fromAbbr] || {}), [playerName]: toAbbr },
    }));
  }, []);

  const setPickDest = useCallback((fromAbbr, pickId, toAbbr) => {
    setPickDestinations(prev => ({
      ...prev,
      [fromAbbr]: { ...(prev[fromAbbr] || {}), [pickId]: toAbbr },
    }));
  }, []);

  // Build trade data for validation
  const activeTeams = selectedTeams.filter(Boolean);
  const tradeResult = useMemo(() => {
    if (activeTeams.length < 2) return null;

    // Need at least 1 player OR pick from each team
    const hasPkgs = activeTeams.every(abbr =>
      (packages[abbr]?.length > 0) || (pickPackages[abbr]?.length > 0)
    );
    if (!hasPkgs) return null;

    // Need at least 1 team sending players (picks alone don't need salary matching but there must be salary if players move)
    const anyPlayers = activeTeams.some(abbr => packages[abbr]?.length > 0);

    // If only picks being traded, it's always valid (no salary matching needed)
    if (!anyPlayers) {
      const breakdown = {};
      activeTeams.forEach(abbr => {
        const sd = salaryData[abbr];
        breakdown[abbr] = {
          outgoing: 0, incoming: 0, maxAllowed: Infinity,
          method: 'Picks only', currentPayroll: sd?.payroll || 0,
          postPayroll: sd?.payroll || 0, netChange: 0,
        };
      });
      // Add NTC warnings for picks-only trades (shouldn't have any but just in case)
      return { valid: true, errors: [], warnings: [], breakdown };
    }

    const tradeTeams = {};
    const tradePackages = {};

    for (const abbr of activeTeams) {
      const sd = salaryData[abbr];
      tradeTeams[abbr] = {
        payroll: sd?.payroll || 0,
        hardCap: getHardCap(abbr),
        players: sd?.players || [],
      };
      tradePackages[abbr] = packages[abbr] || [];
    }

    if (activeTeams.length > 2 && Object.keys(destinations).length > 0) {
      // Pool validation for 3-team trades
    }

    const result = validateTrade({ teams: tradeTeams, packages: tradePackages, phase: seasonPhase });

    // Add NTC warnings
    for (const abbr of activeTeams) {
      const teamPlayers = packages[abbr] || [];
      for (const p of teamPlayers) {
        const ntc = hasNoTradeClause(p.name);
        if (ntc) {
          result.warnings.push(`${p.name} has a ${ntc.type} — trade requires player consent`);
        }
      }
    }

    return result;
  }, [activeTeams, packages, pickPackages, salaryData, destinations, seasonPhase]);

  const handleExecute = useCallback(() => {
    if (!tradeResult?.valid) return;

    // Only execute salary swap if players are involved
    const anyPlayers = activeTeams.some(abbr => packages[abbr]?.length > 0);
    const newData = anyPlayers
      ? executeTrade(salaryData, packages, activeTeams.length === 2 ? null : destinations)
      : JSON.parse(JSON.stringify(salaryData));

    const tradeRecord = {
      packages: JSON.parse(JSON.stringify(packages)),
      picks: JSON.parse(JSON.stringify(pickPackages)),
      pickDestinations: JSON.parse(JSON.stringify(pickDestinations)),
      teams: [...activeTeams],
      timestamp: Date.now(),
    };

    setPackages({});
    setPickPackages({});
    setDestinations({});
    setPickDestinations({});

    onTradeExecuted(newData, tradeRecord);
  }, [tradeResult, activeTeams, packages, pickPackages, destinations, pickDestinations, salaryData, onTradeExecuted]);

  const handleReset = useCallback(() => {
    setPackages({});
    setPickPackages({});
    setDestinations({});
    setPickDestinations({});
  }, []);

  // Suggest-a-Fix: find minimum-salary filler to balance a failing trade
  const suggestFix = useCallback(() => {
    if (!tradeResult || tradeResult.valid) return null;
    // Check each team's breakdown to find who's receiving too much
    const bd = tradeResult.breakdown;
    if (!bd) return null;

    const suggestions = [];

    for (const abbr of activeTeams) {
      const entry = bd[abbr];
      if (!entry) continue;
      // If incoming > maxAllowed, this team needs to send more salary out
      if (entry.incoming > entry.maxAllowed) {
        const deficit = entry.incoming - entry.maxAllowed;
        // Find cheap players on this team not already in the trade
        const teamPlayers = salaryData[abbr]?.players || [];
        const alreadySending = (packages[abbr] || []).map(p => p.name);
        const candidates = teamPlayers
          .filter(p => !alreadySending.includes(p.name) && p.salary2526 > 0 && !p.isTwoWay)
          .sort((a, b) => a.salary2526 - b.salary2526);

        let accumulated = 0;
        const fillers = [];
        for (const c of candidates) {
          if (accumulated >= deficit) break;
          fillers.push(c);
          accumulated += c.salary2526;
        }
        if (fillers.length > 0) {
          suggestions.push({ team: abbr, players: fillers, deficit });
        }
      }
    }
    return suggestions.length > 0 ? suggestions : null;
  }, [tradeResult, activeTeams, packages, salaryData]);

  const applySuggestion = useCallback((suggestion) => {
    setPackages(prev => {
      const next = { ...prev };
      const existing = next[suggestion.team] || [];
      const newPlayers = suggestion.players.filter(p => !existing.find(e => e.name === p.name));
      next[suggestion.team] = [...existing, ...newPlayers];
      return next;
    });
  }, []);

  // Teams available for selection (not already selected in another slot)
  const getAvailableTeams = (currentIndex) => {
    const taken = selectedTeams.filter((t, i) => i !== currentIndex && t);
    return allTeams.filter(t => !taken.includes(t.abbr));
  };

  return (
    <div className="fade-in">
      <div className="section-header">
        <div className="section-label">Build Deals</div>
        <div className="section-title">Trade Machine</div>
        <div className="section-desc">
          Select teams, click players to add them to the trade. Salary matching validates in real-time.
        </div>
      </div>

      {/* Post-Deadline / Playoffs Lock */}
      {(seasonPhase === 'postDeadline' || seasonPhase === 'playoffs') && (
        <div style={{ 
          textAlign: 'center', padding: '48px 20px', 
          border: '1px solid var(--border)', borderRadius: 4,
          background: 'var(--bg-alt)', marginBottom: 24
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text-muted)', marginBottom: 6 }}>
            {seasonPhase === 'postDeadline' ? 'Trade Deadline Has Passed' : 'Playoffs In Progress'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', maxWidth: 340, margin: '0 auto' }}>
            {seasonPhase === 'postDeadline'
              ? 'The NBA trade deadline was February 6, 2026. Advance to the Offseason to make trades.'
              : 'Rosters are locked during the playoffs. Advance to the Offseason to resume trades.'}
          </div>
        </div>
      )}

      {seasonPhase !== 'postDeadline' && seasonPhase !== 'playoffs' && <>
      {/* Team Selectors */}
      <div className="trade-team-selectors">
        {selectedTeams.map((abbr, i) => (
          <div key={i} className="trade-team-selector">
            <TeamPicker
              value={abbr}
              options={getAvailableTeams(i)}
              onChange={(val) => handleTeamSelect(i, val)}
              placeholder={`Select Team ${i + 1}`}
            />
            {i >= 2 && (
              <button className="trade-remove-team-btn" onClick={() => removeTeam(i)}>
                &times;
              </button>
            )}
          </div>
        ))}
        {selectedTeams.length < 6 && (
          <button className="trade-add-team-btn" onClick={addTeam}>
            + Add Team
          </button>
        )}
      </div>

      {/* Trade Columns */}
      <div className="trade-columns" style={{ gridTemplateColumns: `repeat(${activeTeams.length || 2}, 1fr)` }}>
        {selectedTeams.map((abbr, i) => {
          if (!abbr) {
            return (
              <div key={i} className="trade-column trade-column-empty">
                <div className="trade-col-empty-text">Select a team above</div>
              </div>
            );
          }
          const team = allTeams.find(t => t.abbr === abbr);
          const otherTeams = activeTeams
            .filter(a => a !== abbr)
            .map(a => allTeams.find(t => t.abbr === a))
            .filter(Boolean);

          return (
            <TeamColumn
              key={abbr}
              team={team}
              teamInfo={team}
              salaryData={salaryData[abbr]}
              sending={packages[abbr] || []}
              sendingPicks={pickPackages[abbr] || []}
              onTogglePlayer={togglePlayer}
              onTogglePick={togglePick}
              otherTeams={otherTeams}
              destinations={destinations}
              pickDestinations={pickDestinations}
              onSetDestination={setDestination}
              onSetPickDestination={setPickDest}
              isMultiTeam={isMultiTeam}
              allTeams={allTeams}
              tradeHistory={tradeHistory}
            />
          );
        })}
      </div>

      {/* Trade Verdict */}
      <TradeVerdict
        result={tradeResult}
        onExecute={handleExecute}
        onReset={handleReset}
        suggestions={suggestFix()}
        onApplySuggestion={applySuggestion}
      />

      {/* Reset Button */}
      {Object.values(packages).some(p => p?.length > 0) && (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <button className="trade-reset-btn" onClick={handleReset}>
            Clear Trade
          </button>
        </div>
      )}

      {/* Trade History */}
      <TradeHistory history={tradeHistory} />

      {/* Trade Finder */}
      <TradeFinder allTeams={allTeams} salaryData={salaryData} seasonPhase={seasonPhase} myTeam={myTeam} onLoadTrade={(teamA, teamB, pkgA, pkgB) => {
        setSelectedTeams([teamA, teamB]);
        setPackages({ [teamA]: pkgA, [teamB]: pkgB });
        setPickPackages({});
        setDestinations({});
        setPickDestinations({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }} />
      </>}

      {/* Rule Violation Modal */}
      {ruleViolation && (
        <div className="modal-overlay" onClick={() => setRuleViolation(null)}>
          <div className="help-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <button className="modal-close" onClick={() => setRuleViolation(null)}>&times;</button>
            <div className="help-modal-header">
              <div className="help-modal-label" style={{ color: 'var(--accent-red)' }}>CBA Violation</div>
              <div className="help-modal-title">Trade Blocked</div>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <div className="help-section-text" style={{ fontSize: 13, lineHeight: 1.7 }}>{ruleViolation}</div>
              </div>
            </div>
            <div style={{ padding: '12px 0 0', textAlign: 'center' }}>
              <button className="welcome-dismiss" onClick={() => setRuleViolation(null)} style={{ padding: '10px 36px' }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeMachine;
